import { DownloadFormat, downloadSpreadsheet } from '@/utils/downloadUtils';
import { CloudUpload } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  CellError,
  ParsedTaxonomyImport,
  TaxonomyImportValidationError,
  parseTaxonomyImportFile,
} from './taxonomyImportParser';
import { getTaxonomyImportTemplateData } from './taxonomyImportTemplateData';
import { TaxonomyImportPageObjectModel } from '@/poms/taxonomyImportPageObjectModel';

const Dropzone = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const downloadTemplate = (format: DownloadFormat) => {
  const data = getTaxonomyImportTemplateData();
  downloadSpreadsheet(data, { filename: `taxonomy-import-template.${format}`, format });
};

interface ImportUploadStepProperties {
  onFileParsed: (data: ParsedTaxonomyImport) => void;
  onClose: () => void;
  externalError?: string;
}

export const ImportUploadStep = ({ onFileParsed, onClose, externalError }: ImportUploadStepProperties) => {
  const [parseError, setParseError] = useState<string | undefined>();
  const [cellErrors, setCellErrors] = useState<CellError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setIsProcessing(true);
      setParseError(undefined);
      setCellErrors([]);

      parseTaxonomyImportFile(file)
        .then(result => {
          onFileParsed(result);
        })
        .catch((error: unknown) => {
          if (error instanceof TaxonomyImportValidationError) {
            setParseError(error.message);
            setCellErrors(error.errors);
          } else {
            setParseError(error instanceof Error ? error.message : 'Failed to parse file');
          }
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [onFileParsed]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: isProcessing,
  });

  return (
    <>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Upload a CSV or Excel file with your taxonomy hierarchy. Each column represents a level in the tree.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Link
              component='button'
              onClick={() => downloadTemplate('csv')}
              sx={{ fontSize: '14px', cursor: 'pointer' }}
            >
              Download CSV template
            </Link>
            <Typography variant='body2' color='text.secondary'>
              or
            </Typography>
            <Link
              component='button'
              onClick={() => downloadTemplate('xlsx')}
              sx={{ fontSize: '14px', cursor: 'pointer' }}
            >
              XLSX template
            </Link>
          </Box>
        </Box>
        {externalError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {externalError}
          </Alert>
        )}
        {parseError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              {parseError}
            </Typography>
            {cellErrors.length > 0 && (
              <Box
                component='ul'
                sx={{
                  mt: 1,
                  mb: 0,
                  pl: 2,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  '& li': { mb: 0.5 },
                }}
              >
                {cellErrors.map(error => (
                  <Typography component='li' variant='body2' key={`${error.row}-${error.column}`}>
                    <strong>
                      Row {error.row}, Level {error.column}
                    </strong>
                    {error.value && <> ({error.value})</>}: {error.message}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}
        <Dropzone
          {...getRootProps()}
          sx={isDragActive ? { borderColor: 'primary.main', backgroundColor: 'action.hover' } : {}}
        >
          <input {...getInputProps()} data-testid={TaxonomyImportPageObjectModel.DropzoneInput} />
          <CloudUpload sx={{ fontSize: '48px', color: 'text.disabled', mb: 1 }} />
          <Typography variant='body1' sx={{ mb: 0.5 }}>
            {isDragActive ? 'Drop the file here' : 'Drag & drop a file here, or click to select'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Supported formats: CSV, XLSX, XLS
          </Typography>
          {isProcessing && <CircularProgress size={24} sx={{ mt: 1 }} />}
        </Dropzone>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid={TaxonomyImportPageObjectModel.UploadCancelButton}>
          Cancel
        </Button>
      </DialogActions>
    </>
  );
};
